import React, {
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import moment from 'moment';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';

import {ApiContext} from '../../../../api/Api';
import Button from '../../../../elements/CustomButton/CustomButton';
import {NotifierContext} from '../../../../utils/Notifier';
import {employeeFullName} from '../../../../utils/modelUtils/alma/employeeUtils';
import {
    formatRequirementDate,
    requirementBadgeStyle
} from '../../../../utils/modelUtils/technology/developmentRequirementUtils';

const loadingId =
    '@Technology.DevelopmentRequirementDetail.developmentRequirements.getOne';

const panelStyle = {
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
};

const headingStyle = {
    background: '#f8f9fb',
    borderRadius: '8px 8px 0 0',
    padding: '16px 20px'
};

const DevelopmentRequirementDetail = ({history}) => {
    const {id} = useParams();
    const api = useContext(ApiContext);
    const notifier = useContext(NotifierContext);

    const me = useSelector(state => state.api.me);

    const loading = useSelector(
        state => Boolean(state.loadingIds[loadingId])
    );

    const [requirement, setRequirement] = useState();
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let active = true;

        setNotFound(false);
        setRequirement(undefined);

        api.developmentRequirements
            .getOne({
                id,
                loadingId,
                customProp: 'developmentRequirementDetail'
            })
            .then(response => {
                if (!active) {
                    return;
                }

                const loadedRequirement =
                    response && response.data
                        ? response.data
                        : response;

                setRequirement(loadedRequirement);
            })
            .catch(() => {
                if (!active) {
                    return;
                }

                setNotFound(true);

                notifier.error(
                    'No fue posible cargar el detalle del requerimiento.'
                );
            });

        return () => {
            active = false;
        };
    }, [api, id, notifier]);

    const creatorId = useMemo(() => {
        if (!requirement || !requirement.createdBy) {
            return null;
        }

        if (
            typeof requirement.createdBy === 'object' &&
            requirement.createdBy.id
        ) {
            return Number(requirement.createdBy.id);
        }

        const match = String(requirement.createdBy).match(/\/(\d+)$/);

        return match ? Number(match[1]) : null;
    }, [requirement]);

    const creatorName = useMemo(() => {
        if (!creatorId) {
            return 'Sin información';
        }

        if (
            me &&
            Number(me.id) === creatorId &&
            me.employee
        ) {
            return employeeFullName(me.employee);
        }

        return `Usuario ${creatorId}`;
    }, [creatorId, me]);

    const goBack = () => {
        history.push('/technology/development-requests');
    };

    if (loading && !requirement) {
        return (
            <div className="container-fluid main-container">
                <div
                    className="text-center"
                    style={{padding: '80px 20px'}}
                >
                    <i
                        className="fa fa-spinner fa-spin"
                        style={{
                            fontSize: '42px',
                            color: '#337ab7',
                            marginBottom: '18px'
                        }}
                    />

                    <h4>Cargando requerimiento...</h4>
                </div>
            </div>
        );
    }

    if (notFound || (!loading && !requirement)) {
        return (
            <div className="container-fluid main-container">
                <div
                    style={{
                        maxWidth: '850px',
                        margin: '40px auto'
                    }}
                >
                    <div className="alert alert-danger">
                        <h4>
                            <i
                                className="fa fa-exclamation-triangle"
                                style={{marginRight: '9px'}}
                            />
                            No fue posible mostrar el requerimiento
                        </h4>

                        <p>
                            El registro no existe o no tienes permisos para
                            consultarlo.
                        </p>
                    </div>

                    <Button bsStyle="default" onClick={goBack}>
                        <i
                            className="fa fa-arrow-left"
                            style={{marginRight: '7px'}}
                        />
                        Regresar al listado
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid main-container">
            <div
                style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    paddingBottom: '40px'
                }}
            >
                <div
                    className="clearfix"
                    style={{
                        marginBottom: '24px',
                        borderBottom: '1px solid #e5e5e5',
                        paddingBottom: '20px'
                    }}
                >
                    <div className="pull-left">
                        <div
                            className="text-muted"
                            style={{
                                fontSize: '15px',
                                fontWeight: 600,
                                marginBottom: '5px'
                            }}
                        >
                            {requirement.folio}
                        </div>

                        <h2
                            style={{
                                marginTop: 0,
                                marginBottom: '12px',
                                lineHeight: 1.25
                            }}
                        >
                            {requirement.title}
                        </h2>

                        <div>
                            <span
                                style={{
                                    ...requirementBadgeStyle(
                                        requirement.status
                                    ),
                                    marginRight: '8px'
                                }}
                            >
                                {requirement.status
                                    ? requirement.status.name
                                    : 'Sin estado'}
                            </span>

                            <span
                                style={{
                                    ...requirementBadgeStyle(
                                        requirement.priority
                                    ),
                                    marginRight: '8px'
                                }}
                            >
                                {requirement.priority
                                    ? requirement.priority.name
                                    : 'Sin prioridad'}
                            </span>

                            <span
                                className="label label-default"
                                style={{
                                    display: 'inline-block',
                                    fontSize: '12px',
                                    padding: '7px 12px',
                                    borderRadius: '14px'
                                }}
                            >
                                {requirement.system
                                    ? requirement.system.name
                                    : 'Sin sistema'}
                            </span>
                        </div>
                    </div>

                    <div className="pull-right">
                        <Button
                            bsStyle="default"
                            onClick={goBack}
                        >
                            <i
                                className="fa fa-arrow-left"
                                style={{marginRight: '7px'}}
                            />
                            Regresar al listado
                        </Button>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-8">
                        <div
                            className="panel panel-default"
                            style={panelStyle}
                        >
                            <div
                                className="panel-heading"
                                style={headingStyle}
                            >
                                <h4 style={{margin: 0}}>
                                    <i
                                        className="fa fa-briefcase"
                                        style={{marginRight: '9px'}}
                                    />
                                    Necesidad del negocio
                                </h4>
                            </div>

                            <div
                                className="panel-body"
                                style={{
                                    padding: '24px',
                                    minHeight: '180px'
                                }}
                            >
                                <p
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.7,
                                        fontSize: '15px',
                                        margin: 0
                                    }}
                                >
                                    {requirement.businessNeed}
                                </p>
                            </div>
                        </div>

                        <div
                            className="panel panel-default"
                            style={panelStyle}
                        >
                            <div
                                className="panel-heading"
                                style={headingStyle}
                            >
                                <h4 style={{margin: 0}}>
                                    <i
                                        className="fa fa-check-circle"
                                        style={{marginRight: '9px'}}
                                    />
                                    Resultado esperado
                                </h4>
                            </div>

                            <div
                                className="panel-body"
                                style={{
                                    padding: '24px',
                                    minHeight: '180px'
                                }}
                            >
                                <p
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.7,
                                        fontSize: '15px',
                                        margin: 0
                                    }}
                                >
                                    {requirement.expectedResult}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div
                            className="panel panel-default"
                            style={panelStyle}
                        >
                            <div
                                className="panel-heading"
                                style={headingStyle}
                            >
                                <h4 style={{margin: 0}}>
                                    <i
                                        className="fa fa-info-circle"
                                        style={{marginRight: '9px'}}
                                    />
                                    Información general
                                </h4>
                            </div>

                            <div
                                className="panel-body"
                                style={{padding: '10px 22px'}}
                            >
                                <DetailRow
                                    label="Folio"
                                    value={requirement.folio}
                                />

                                <DetailRow
                                    label="Sistema"
                                    value={
                                        requirement.system
                                            ? requirement.system.name
                                            : 'Sin información'
                                    }
                                />

                                <DetailRow
                                    label="Prioridad"
                                    value={
                                        requirement.priority
                                            ? requirement.priority.name
                                            : 'Sin información'
                                    }
                                />

                                <DetailRow
                                    label="Estado"
                                    value={
                                        requirement.status
                                            ? requirement.status.name
                                            : 'Sin información'
                                    }
                                />

                                <DetailRow
                                    label="Solicitante"
                                    value={creatorName}
                                />

                                <DetailRow
                                    label="Fecha deseada"
                                    value={formatRequirementDate(
                                        requirement.desiredDate,
                                        moment
                                    )}
                                />

                                <DetailRow
                                    label="Creación"
                                    value={
                                        requirement.createdDate
                                            ? moment(
                                                requirement.createdDate
                                            ).format(
                                                'DD/MM/YYYY HH:mm'
                                            )
                                            : 'Sin información'
                                    }
                                />

                                <DetailRow
                                    label="Última actualización"
                                    value={
                                        requirement.updatedDate
                                            ? moment(
                                                requirement.updatedDate
                                            ).format(
                                                'DD/MM/YYYY HH:mm'
                                            )
                                            : 'Sin información'
                                    }
                                    last
                                />
                            </div>
                        </div>

                        <div
                            className="panel panel-default"
                            style={panelStyle}
                        >
                            <div
                                className="panel-heading"
                                style={headingStyle}
                            >
                                <h4 style={{margin: 0}}>
                                    <i
                                        className="fa fa-history"
                                        style={{marginRight: '9px'}}
                                    />
                                    Actividad
                                </h4>
                            </div>

                            <div
                                className="panel-body"
                                style={{padding: '22px'}}
                            >
                                <div
                                    style={{
                                        position: 'relative',
                                        paddingLeft: '30px'
                                    }}
                                >
                                    <span
                                        style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: '4px',
                                            width: '14px',
                                            height: '14px',
                                            borderRadius: '50%',
                                            backgroundColor:
                                                requirement.status &&
                                                requirement.status.color
                                                    ? requirement.status.color
                                                    : '#777777'
                                        }}
                                    />

                                    <strong>
                                        Requerimiento creado
                                    </strong>

                                    <p
                                        className="text-muted"
                                        style={{
                                            marginTop: '5px',
                                            marginBottom: '5px'
                                        }}
                                    >
                                        Se registró inicialmente con estado
                                        Borrador.
                                    </p>

                                    <small className="text-muted">
                                        {requirement.createdDate
                                            ? moment(
                                                requirement.createdDate
                                            ).format(
                                                'DD/MM/YYYY HH:mm'
                                            )
                                            : 'Fecha no disponible'}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className="clearfix"
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e5e5e5',
                        borderRadius: '8px',
                        padding: '16px 20px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                    }}
                >
                    <Button
                        bsStyle="default"
                        className="pull-left"
                        onClick={goBack}
                    >
                        <i
                            className="fa fa-arrow-left"
                            style={{marginRight: '7px'}}
                        />
                        Regresar al listado
                    </Button>

                    <span
                        className="text-muted pull-right"
                        style={{paddingTop: '8px'}}
                    >
                        Expediente {requirement.folio}
                    </span>
                </div>
            </div>
        </div>
    );
};

const DetailRow = ({label, value, last}) => (
    <div
        style={{
            padding: '14px 0',
            borderBottom: last
                ? 'none'
                : '1px solid #eeeeee'
        }}
    >
        <div
            className="text-muted"
            style={{
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                marginBottom: '4px'
            }}
        >
            {label}
        </div>

        <div
            style={{
                fontWeight: 600,
                wordBreak: 'break-word'
            }}
        >
            {value}
        </div>
    </div>
);

export default DevelopmentRequirementDetail;
